import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Platform } from '@multimod/shared';
import { createChatFeedStore, type ChatFeedStore } from './store.js';
import { ChatWsClient } from './ws-client.js';

const stores = new Map<string, ChatFeedStore>();

/**
 * Conjunto de liveChatIds com instância ativa do hook. Garante o contrato
 * "1× por live" (P10/Bug A): qualquer segunda montagem simultânea com a mesma
 * chave joga erro, independente de ambiente. Em prod evita custo de WS extra;
 * em dev expõe regressão de quem importou `useChatFeed` em componente-folha
 * em vez de receber `store` via prop.
 */
const activeOwners = new Set<string>();

function getOrCreateStore(liveChatId: string): ChatFeedStore {
  let s = stores.get(liveChatId);
  if (!s) {
    s = createChatFeedStore();
    stores.set(liveChatId, s);
  }
  return s;
}

/**
 * Lê o store de uma live sem abrir WS. Retorna undefined se a live não está
 * conectada (nenhum useChatFeed ativo pra esse liveChatId).
 * Usado por hooks que apenas observam (ex: useMsgRate) sem abrir WS extra.
 */
export function getChatStore(liveChatId: string): ChatFeedStore | undefined {
  return stores.get(liveChatId);
}

export interface ChatFeedAccess {
  store: ChatFeedStore;
  attach: (opts: { platform: Platform; externalId: string; liveChatId: string }) => void;
  detach: () => void;
}

/**
 * Hook principal de chat feed.
 *
 * Deve ser chamado UMA VEZ por live (em PanelPage/ActiveFeedBridge). Componentes-folha
 * (ChatFeed, ConnectionStatus) recebem `store` via prop para evitar criar WS duplicado.
 *
 * Lifecycle:
 * - mount: cria primary `ChatWsClient` e conecta
 * - liveChatId/platform muda: cleanup local fecha primary + attach antigos, abre novos
 * - unmount: fecha primary + attach, remove store global do map
 *
 * Bug A (2026-05-18): a versão anterior usava refCount + Maps globais por liveChatId.
 * Em produção observamos WS órfão tentando reconectar a um liveChatId Twitch trocado.
 * A reescrita com useRef garante disconnect determinístico via closure local.
 */
export function useChatFeed(liveChatId: string, platform: Platform): ChatFeedAccess {
  // Trava de contrato (render phase pra ser pego por error boundary / renderHook):
  // se algum OUTRO componente já reivindicou esta liveChatId via effect, joga.
  // Render phase porque commit-phase throws sem error boundary só viram
  // console.error em React 18 — não dá pra testar com expect(...).toThrow.
  const ownedLiveChatIdRef = useRef<string | null>(null);
  if (ownedLiveChatIdRef.current !== liveChatId && activeOwners.has(liveChatId)) {
    throw new Error(
      `useChatFeed: já existe instância ativa para liveChatId="${liveChatId}". ` +
        `O contrato é chamar 1× por live (no container, ex.: PanelPage/ActiveFeedBridge); ` +
        `componentes-folha devem receber \`store\` via prop.`,
    );
  }

  const store = useMemo(() => getOrCreateStore(liveChatId), [liveChatId]);
  const attachClientRef = useRef<ChatWsClient | null>(null);
  // Identidade do alvo de attach atual (`platform|externalId|liveChatId`). Guarda
  // a idempotência de attach(): re-attach do MESMO alvo é no-op (não derruba o WS).
  const attachKeyRef = useRef<string | null>(null);

  useEffect(() => {
    activeOwners.add(liveChatId);
    ownedLiveChatIdRef.current = liveChatId;
    // dev-only: liveChatId com prefixo `__mm-mock:` desativa WS — usado pelo
    // DevMockPanel pra validar UI/UX sem servidor real (gated por devMockActions).
    // Prefixo distintivo (não apenas `mock-`) evita colisão com IDs reais de
    // plataforma (YouTube usa strings opacas e Twitch pode aceitar hyphens).
    if (liveChatId.startsWith('__mm-mock:')) {
      return () => {
        activeOwners.delete(liveChatId);
        ownedLiveChatIdRef.current = null;
        stores.delete(liveChatId);
      };
    }
    const client = new ChatWsClient(
      { kind: 'primary', platform, liveChatId },
      store,
    );
    client.connect();
    return () => {
      activeOwners.delete(liveChatId);
      ownedLiveChatIdRef.current = null;
      attachClientRef.current?.disconnect();
      attachClientRef.current = null;
      attachKeyRef.current = null;
      client.disconnect();
      stores.delete(liveChatId);
    };
  }, [liveChatId, platform, store]);

  const attach = useCallback(
    (opts: { platform: Platform; externalId: string; liveChatId: string }) => {
      const key = `${opts.platform}|${opts.externalId}|${opts.liveChatId}`;
      // Idempotência: re-attach do MESMO alvo é no-op. O effect de attach no
      // PanelSession re-dispara a cada patch de viewerCount (referência nova de
      // attachedLives via .map()); sem este guard, cada re-render derrubava o WS
      // de attach → o DO tratava como owner-rotation → deleteAll() → sessão
      // inteira colapsava (queda de WS a cada ~2-3 min em staging).
      if (attachKeyRef.current === key) return;
      attachClientRef.current?.disconnect();
      attachKeyRef.current = key;
      // dev-only: pula WS pra liveChatId __mm-mock:* (igual ao primary). Vê hook.ts:73-79.
      if (opts.liveChatId.startsWith('__mm-mock:')) {
        attachClientRef.current = null;
        return;
      }
      const client = new ChatWsClient(
        {
          kind: 'attach',
          primaryPlatform: platform,
          primaryLiveChatId: liveChatId,
          attachPlatform: opts.platform,
          attachExternalId: opts.externalId,
          attachLiveChatId: opts.liveChatId,
        },
        store,
      );
      attachClientRef.current = client;
      client.connect();
    },
    [liveChatId, platform, store],
  );

  const detach = useCallback(() => {
    attachClientRef.current?.disconnect();
    attachClientRef.current = null;
    attachKeyRef.current = null;
  }, []);

  return useMemo(() => ({ store, attach, detach }), [store, attach, detach]);
}
